import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronLeft, ChevronRight, Lock, Search, Star } from 'lucide-react';
import { buildPageList } from '../../utils/gmpBoard';
import {
  OFFER_META,
  PIPELINE_TABS,
  STATUS_META,
  buildJourney,
  filterFilings,
  formatFilingDate,
  uniqueSorted,
} from '../../utils/upcomingIpos';

const PAGE_SIZE = 12;
const WATCHLIST_KEY = 'alambrix.upcomingIpos.watchlist';
const AVATAR_TONES = [
  'bg-teal-50 text-[#006666]',
  'bg-amber-50 text-amber-700',
  'bg-sky-50 text-sky-700',
  'bg-rose-50 text-rose-700',
  'bg-violet-50 text-violet-700',
  'bg-emerald-50 text-emerald-700',
];

const DEFAULT_FILTERS = { status: 'all', sector: 'all', leadManager: 'all', query: '' };

const initialsOf = (company) =>
  company
    .replace(/\(.*?\)|\bLtd\.?|\bLimited\b/gi, '')
    .split(/[\s.&-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');

// Catalog ids are numeric (GET /api/core/ipos), so stringify before hashing
const avatarTone = (id) => AVATAR_TONES[[...String(id)].reduce((sum, ch) => sum + ch.charCodeAt(0), 0) % AVATAR_TONES.length];

// Starred companies, kept in this browser only
const readWatchlist = () => {
  try {
    return JSON.parse(window.localStorage.getItem(WATCHLIST_KEY)) || [];
  } catch {
    return [];
  }
};

const JourneyDetails = ({ filing, today }) => {
  const steps = buildJourney(filing, today);
  return (
    <div className="px-4 sm:px-5 py-4 bg-slate-50/80 border-t border-slate-100">
      <ol className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {steps.map((step, index) => (
          <li
            key={step.key}
            className={`rounded-xl border p-3 ${
              step.state === 'done'
                ? 'bg-white border-emerald-200'
                : step.state === 'current'
                  ? 'bg-white border-teal-300 ring-1 ring-teal-200'
                  : 'bg-white/60 border-slate-200'
            }`}
          >
            <div className="flex items-center gap-2">
              <span
                className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${
                  step.state === 'done'
                    ? 'bg-emerald-500 text-white'
                    : step.state === 'current'
                      ? 'bg-[#008080] text-white'
                      : 'bg-slate-200 text-slate-500'
                }`}
              >
                {step.state === 'done' ? <Check className="w-3 h-3" /> : index + 1}
              </span>
              <span className="text-xs font-bold text-slate-800">{step.title}</span>
            </div>
            <p className="mt-1 text-[11px] text-slate-500">{step.detail}</p>
          </li>
        ))}
      </ol>
      <p className="mt-3 text-[11px] text-slate-500">
        {filing.size}
        {filing.sizeNote ? ` (${filing.sizeNote})` : ''} • Lead manager: {filing.leadManager} • Listing on{' '}
        {filing.exchanges.join(' & ')}
        {filing.confidential && ' • Filed confidentially: the draft stays private until the company proceeds.'}
      </p>
    </div>
  );
};

const selectClass =
  'w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:border-[#008080] focus:ring-1 focus:ring-[#008080]';

export const PipelineTable = ({ filings, tab, onTabChange, today, onViewDocument, onVisibleChange }) => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);
  const [watchlist, setWatchlist] = useState(readWatchlist);

  const sectors = useMemo(() => uniqueSorted(filings, 'sector'), [filings]);
  const leadManagers = useMemo(() => uniqueSorted(filings, 'leadManager'), [filings]);
  const tabCounts = useMemo(
    () => Object.fromEntries(PIPELINE_TABS.map((t) => [t.key, filings.filter(t.test).length])),
    [filings]
  );
  const rows = useMemo(() => filterFilings(filings, { tab, ...filters }), [filings, tab, filters]);

  useEffect(() => {
    setPage(1);
    setExpanded(null);
  }, [tab, filters]);

  // Let the page export exactly what the table currently shows
  useEffect(() => {
    onVisibleChange(rows);
  }, [rows, onVisibleChange]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const firstIndex = rows.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const lastIndex = Math.min(currentPage * PAGE_SIZE, rows.length);
  const filtersActive = JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS);

  const setFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }));

  const toggleWatch = (id) =>
    setWatchlist((current) => {
      const next = current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
      try {
        window.localStorage.setItem(WATCHLIST_KEY, JSON.stringify(next));
      } catch {
        // Storage can be unavailable (private mode); the star still works for this visit
      }
      return next;
    });

  return (
    <section aria-label="DRHP pipeline" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
      {/* Tabs + count */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 px-4 sm:px-5 pt-4">
        <div role="group" aria-label="Pipeline view" className="flex gap-1 p-1 rounded-xl bg-slate-100 border border-slate-200/80 overflow-x-auto">
          {PIPELINE_TABS.map((option) => {
            const active = option.key === tab;
            return (
              <button
                key={option.key}
                type="button"
                aria-pressed={active}
                onClick={() => onTabChange(option.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                  active ? 'bg-white text-[#008080] shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {option.key === 'confidential' && <Lock className="w-3 h-3" />}
                {option.label} ({tabCounts[option.key]})
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span>
            Showing {firstIndex} to {lastIndex} of {rows.length} entries
          </span>
          <button
            type="button"
            onClick={() => setFilters(DEFAULT_FILTERS)}
            disabled={!filtersActive}
            className="font-bold text-[#008080] hover:underline disabled:text-slate-300 disabled:no-underline disabled:cursor-default"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 px-4 sm:px-5 py-4 border-b border-slate-100">
        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Filing Status</span>
          <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)} className={selectClass}>
            <option value="all">All statuses</option>
            <option value="approved">SEBI / exchange approval received</option>
            <option value="under_review">Under SEBI review</option>
            <option value="pre_filing">Confidential pre-filing</option>
          </select>
        </label>
        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Sector / Industry</span>
          <select value={filters.sector} onChange={(e) => setFilter('sector', e.target.value)} className={selectClass}>
            <option value="all">All industries &amp; sectors</option>
            {sectors.map((sector) => (
              <option key={sector} value={sector}>
                {sector}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Book Running Lead Manager</span>
          <select value={filters.leadManager} onChange={(e) => setFilter('leadManager', e.target.value)} className={selectClass}>
            <option value="all">All lead managers</option>
            {leadManagers.map((manager) => (
              <option key={manager} value={manager}>
                {manager}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Search</span>
          <span className="relative block">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={filters.query}
              onChange={(e) => setFilter('query', e.target.value)}
              placeholder="Quick search…"
              className={`${selectClass} pl-8 font-medium placeholder-slate-400`}
            />
          </span>
        </label>
      </div>

      {/* Table */}
      <div className="overflow-x-auto scrollbar-slim">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50/90 border-b border-slate-200/80 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
              <th className="sticky left-0 z-10 bg-slate-50 py-3 pl-4 sm:pl-5 pr-3 min-w-[240px]">Company &amp; Industry</th>
              <th className="py-3 px-3 whitespace-nowrap">Offer Structure</th>
              <th className="py-3 px-3 whitespace-nowrap">DRHP Filing Date</th>
              <th className="py-3 px-3 whitespace-nowrap">Approval Date</th>
              <th className="py-3 px-3 whitespace-nowrap">Estimated Size</th>
              <th className="py-3 px-3 whitespace-nowrap">Exchange</th>
              <th className="py-3 px-3 whitespace-nowrap">Lead Manager</th>
              <th className="py-3 px-3 whitespace-nowrap">Status / Stage</th>
              <th className="py-3 pl-3 pr-4 sm:pr-5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="text-xs sm:text-[13px]">
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={9} className="py-12 text-center text-sm text-slate-500">
                  No filings match these filters.{' '}
                  <button type="button" onClick={() => setFilters(DEFAULT_FILTERS)} className="font-bold text-[#008080] hover:underline">
                    Reset filters
                  </button>
                </td>
              </tr>
            )}
            {pageRows.map((filing) => {
              const isOpen = expanded === filing.id;
              const watched = watchlist.includes(filing.id);
              const rowTone = filing.featured ? 'bg-teal-50/50' : 'bg-white';
              return (
                <Fragment key={filing.id}>
                  <tr className={`group border-t border-slate-100 ${rowTone} hover:bg-slate-50/80 transition-colors`}>
                    <td className={`sticky left-0 z-10 py-3 pl-4 sm:pl-5 pr-3 ${rowTone} group-hover:bg-slate-50`}>
                      <div className="flex items-center gap-3">
                        <span
                          className={`w-9 h-9 rounded-lg flex items-center justify-center text-[11px] font-extrabold flex-shrink-0 ${
                            filing.confidential ? 'bg-slate-100 text-slate-500' : avatarTone(filing.id)
                          }`}
                        >
                          {filing.confidential ? <Lock className="w-4 h-4" /> : initialsOf(filing.company)}
                        </span>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="font-bold text-slate-900 leading-snug">{filing.company}</span>
                            {filing.tag && (
                              <span className="px-1.5 py-px rounded bg-amber-400 text-amber-950 text-[9px] font-extrabold uppercase tracking-wide">
                                {filing.tag}
                              </span>
                            )}
                            {filing.confidential && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-px rounded border border-violet-200 bg-violet-50 text-violet-700 text-[9px] font-bold uppercase">
                                <Lock className="w-2.5 h-2.5" />
                                Confidential
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-400 mt-0.5">{filing.sector}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-md border text-[10px] font-bold ${OFFER_META[filing.offer].badge}`}>
                        {OFFER_META[filing.offer].label}
                      </span>
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap text-slate-600">{formatFilingDate(filing.filedOn)}</td>
                    <td className="py-3 px-3 whitespace-nowrap">
                      {filing.approvedOn ? (
                        <span className="font-semibold text-slate-800">{formatFilingDate(filing.approvedOn)}</span>
                      ) : (
                        <span className="text-slate-400">Awaiting</span>
                      )}
                    </td>
                    <td className="py-3 px-3 min-w-[150px]">
                      <div className={`font-semibold ${filing.featured ? 'text-[#008080]' : 'text-slate-800'}`}>{filing.size}</div>
                      {filing.sizeNote && <div className="text-[11px] text-slate-400">{filing.sizeNote}</div>}
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap">
                      <div className="flex gap-1">
                        {filing.exchanges.map((exchange) => (
                          <span key={exchange} className="px-1.5 py-0.5 rounded border border-slate-200 bg-white text-[10px] font-bold text-slate-600">
                            {exchange}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-3 min-w-[130px] text-slate-600">{filing.leadManager}</td>
                    <td className="py-3 px-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg border text-[11px] font-bold ${STATUS_META[filing.status].badge}`}>
                        {filing.status === 'approved' && <Check className="w-3 h-3" />}
                        {STATUS_META[filing.status].label}
                      </span>
                    </td>
                    <td className="py-3 pl-3 pr-4 sm:pr-5">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => toggleWatch(filing.id)}
                          aria-pressed={watched}
                          aria-label={`${watched ? 'Remove' : 'Add'} ${filing.company} ${watched ? 'from' : 'to'} watchlist`}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                            watched ? 'text-amber-500 hover:bg-amber-50' : 'text-slate-300 hover:text-amber-500 hover:bg-slate-100'
                          }`}
                        >
                          <Star className={`w-4 h-4 ${watched ? 'fill-amber-400' : ''}`} />
                        </button>
                        {filing.featured && (
                          <button
                            type="button"
                            onClick={() => onViewDocument(filing)}
                            className="px-2.5 py-1.5 rounded-lg bg-[#008080] hover:bg-[#006666] text-white text-[11px] font-bold whitespace-nowrap"
                          >
                            View DRHP
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setExpanded(isOpen ? null : filing.id)}
                          aria-expanded={isOpen}
                          aria-controls={`filing-details-${filing.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-[11px] font-bold text-slate-600 hover:border-slate-300 whitespace-nowrap"
                        >
                          Details
                          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr id={`filing-details-${filing.id}`}>
                      <td colSpan={9} className="p-0">
                        <JourneyDetails filing={filing} today={today} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-t border-slate-200/80 bg-slate-50/70">
        <p className="text-xs text-slate-500">
          Showing {firstIndex} to {lastIndex} of {rows.length} entries ({PIPELINE_TABS.find((t) => t.key === tab).label})
        </p>
        {totalPages > 1 && (
          <nav className="flex items-center gap-1" aria-label="Pipeline pagination">
            <button
              type="button"
              onClick={() => setPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Prev
            </button>
            {buildPageList(currentPage, totalPages).map((p, i) =>
              p === '…' ? (
                <span key={`gap-${i}`} className="w-7 text-center text-xs text-slate-400">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  aria-current={p === currentPage ? 'page' : undefined}
                  className={`w-8 h-8 rounded-lg text-xs font-bold ${
                    p === currentPage ? 'bg-[#008080] text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {p}
                </button>
              )
            )}
            <button
              type="button"
              onClick={() => setPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </nav>
        )}
      </div>
    </section>
  );
};

export default PipelineTable;
